import "./style.scss";

import ModalBox from "components/Modal";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { IStatementTemplate } from "store/partnerStatementsStore";
import { createStatementTemplate, setFetchCreatePartnerStatementStatus } from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

/**
 * Chose a name for the statement, last step before sending the form
 */
interface ISubmitFormModal {
    open: boolean;
    onClose: () => void;
    form: INewStatementForm;
}

const SubmitFormModal = ({ open, onClose, form }: ISubmitFormModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        control,
        getValues,
        formState: { errors },
    } = useFormContext<INewStatementForm>();

    const fetchCreatePartnerStatementStatus = useAppSelector(
        (state) => state.partnerStatements.fetchCreatePartnerStatementStatus,
    );
    const [isLoading, setIsLoading] = useState(false);

    const onSubmitForm = async () => {
        setIsLoading(true);
        const name = getValues("name");

        const reqData: IStatementTemplate = {
            name: name?.trim() as string,
            integrationId: form.integration?.id as number,
            type: form.type,
            data: {
                spreadsheetId: form.spreadsheetId as string,
                sheetId: form.sheetId as number,
                iterationRange: form.iterationRange as string,
                variables: form.variables as Record<string, string>,
            },
        };
        const response = await dispatch(createStatementTemplate(reqData));

        if (response.meta.requestStatus === "fulfilled") {
            // should put navigate here, but it still doesn't work
            setIsLoading(false);
        } else if (response.meta.requestStatus === "rejected") {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (fetchCreatePartnerStatementStatus === "succeeded") {
            navigate("/settings/partner-statements");
            dispatch(setFetchCreatePartnerStatementStatus("idle"));
        }
    }, [fetchCreatePartnerStatementStatus]);

    return (
        <ModalBox mainTitle="Validation" open={open} onClose={onClose}>
            <div className="create-statement-submit-modal">
                <div className="create-statement-submit-modal-head">
                    <h2>{t("PartnerStatements.CreatedSoon")}</h2>
                    <span>{t("PartnerStatements.TypeTitle")}</span>
                </div>
                <TextFormComponent
                    control={control}
                    controlName="name"
                    label={t("PartnerStatements.StatementName")}
                    error={errors.name}
                    placeholder={t("PartnerStatements.TypeName")}
                    required
                />
                <div className="create-statement-submit-modal-btn">
                    <StyledButton
                        buttonType="primary"
                        onClick={onSubmitForm}
                        fullWidth
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        {!isLoading && t("PartnerStatements.ValidateCreation")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default SubmitFormModal;
